import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useEditorStore } from '@/stores/editor/editor-store';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Square,
  Type,
  Image,
  Package,
  Table,
  BarChart3,
  Layout,
  MousePointer,
  Sparkles,
  ChevronRight
} from 'lucide-react';

const componentTypes = [
  { value: 'auto', label: 'Auto-detect', icon: Sparkles },
  { value: 'text', label: 'Text', icon: Type },
  { value: 'button', label: 'Button', icon: MousePointer },
  { value: 'image', label: 'Image', icon: Image },
  { value: 'container', label: 'Container', icon: Package },
  { value: 'table', label: 'Table', icon: Table },
  { value: 'line-chart', label: 'Line Chart', icon: BarChart3 },
  { value: 'bar-chart', label: 'Bar Chart', icon: BarChart3 },
  { value: 'pie-chart', label: 'Pie Chart', icon: BarChart3 },
  { value: 'gauge-chart', label: 'Gauge', icon: BarChart3 },
  { value: 'flex-container', label: 'Flex Container', icon: Layout },
];

interface SketchAnnotationPanelProps {
  onSelectComponentType?: () => void;
}

export const SketchAnnotationPanel: React.FC<SketchAnnotationPanelProps> = ({ onSelectComponentType }) => {
  const {
    sketches,
    selectedSketchId,
    sketchAnnotations,
    updateSketchAnnotation,
    setSelectingComponentForSketch,
  } = useEditorStore();

  const [componentType, setComponentType] = useState<string>('auto');
  const [description, setDescription] = useState<string>('');

  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const selectedSketch = sketches.find(s => s.id === selectedSketchId);
  const existingAnnotation = selectedSketchId ? sketchAnnotations.get(selectedSketchId) : undefined;

  useEffect(() => {
    if (existingAnnotation) {
      setComponentType(existingAnnotation.componentType || 'auto');
      setDescription(existingAnnotation.text || '');
    } else {
      setComponentType('auto');
      setDescription('');
    }
  }, [selectedSketchId, existingAnnotation]);

  // Update component type when it's changed via the store (from palette selection)
  useEffect(() => {
    if (existingAnnotation?.componentType && existingAnnotation.componentType !== componentType) {
      setComponentType(existingAnnotation.componentType);
    }
  }, [existingAnnotation?.componentType, componentType]);


  const handleSelectComponentClick = useCallback(() => {
    if (selectedSketchId) {
      // Save current annotation state
      updateSketchAnnotation(selectedSketchId, {
        rectangleId: selectedSketchId,
        text: description,
        componentType: componentType,
        properties: {},
        autoDetected: componentType === 'auto',
      });

      // Set the selecting mode and call the parent callback
      setSelectingComponentForSketch(true);
      if (onSelectComponentType) {
        onSelectComponentType();
      }
    }
  }, [selectedSketchId, description, componentType, updateSketchAnnotation, setSelectingComponentForSketch, onSelectComponentType]);

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setDescription(value);
    console.log('📝 Description changed:', value);

    // Debounced auto-save for description
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (selectedSketchId) {
        console.log('💾 Auto-saving description:', { selectedSketchId, value });

        // Keep the component type as-is for auto-save
        const effectiveType = componentType;

        updateSketchAnnotation(selectedSketchId, {
          rectangleId: selectedSketchId,
          text: value,
          componentType: effectiveType,
          properties: {},
          autoDetected: componentType === 'auto',
        });
        console.log('✅ Auto-save completed for description');
      }
    }, 500); // 500ms delay
  }, [selectedSketchId, componentType, updateSketchAnnotation]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  if (!selectedSketch) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <Square className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Select a sketch to annotate</p>
      </div>
    );
  }

  const getComponentIcon = (type: string) => {
    const component = componentTypes.find(c => c.value === type);
    const Icon = component?.icon || Square;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <Card className="p-4 overflow-y-auto max-h-full">
      <div className="space-y-4">
        <div>
          <h3 className="font-medium mb-2">Sketch Annotation</h3>
          <p className="text-xs text-muted-foreground">
            Define what this component should be
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="component-type">Component Type</Label>
            <Button
              id="component-type"
              variant="outline"
              className="w-full justify-between"
              onClick={handleSelectComponentClick}
            >
              <div className="flex items-center gap-2">
                {getComponentIcon(componentType)}
                <span>{componentTypes.find(c => c.value === componentType)?.label}</span>
              </div>
              <ChevronRight className="w-4 h-4" />
            </Button>
            <p className="text-xs text-muted-foreground mt-1">
              Click to select a component type from the palette
            </p>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <textarea
              id="description"
              value={description}
              onChange={handleDescriptionChange}
              placeholder="E.g., Water Metrics title with bold grey text"
              className="flex min-h-[150px] w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
              style={{ resize: 'vertical' }}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Describe what this component should contain or display
            </p>

            {/* Auto-detection will happen when generate button is pressed */}
            {componentType === 'auto' && (
              <p className="text-xs text-muted-foreground mt-1">
                Component type will be detected automatically when you generate the component
              </p>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
